<template>
  <ConfirmDialog :style="{ width: '80vw', maxWidth: '700px' }" />

  <FullModal
    :headerInfo="headerInfo"
    @onClose="confirmModal()"
    :hasIcon="true"
    :hiddenCloseButton="quizEnd"
    :closeDanger="true"
    class="bg-['#F8F9FA']"
  >
    <template #content>
      <main
        v-motion
        :initial="initialMotion"
        :enter="enterMotion"
        class="flex gap-10 justify-center items-center md:items-start select-none my-4 md:px-2"
      >
        <section
          v-if="isAdaptative ? !quizEnded : !storeQuiz.getQuizHasEnded"
          id="init-quiz"
          class="flex flex-col gap-3 w-11/12 sm:w-11/12 lg:w-8/12 xl:w-8/12"
        >
          <div class="flex justify-between items-center">
            <div class="md:min-w-[180px] w-[0%] md:w-[18%]"></div>
            <div class="w-[100%] md:w-[90%] flex justify-between items-center">
              <p>
                {{ questionCount + 1 }}/ {{ quiz?.questionList?.length }}
                {{
                  $t("labels.generic", { pt: " Questões", en: " Questions" })
                }}
              </p>
              <div class="flex gap-4 items-center">
                <CountDown
                  :time="quizIntro?.duration * 1000"
                  v-slot="{ hours, minutes, seconds }"
                  @end="onCountdownEnd()"
                >
                  <div
                    class="flex items-center gap-1 bg-white border justify-center p-3 rounded-md shadow-sm"
                  >
                    <v-icon
                      name="md-watchlater-round"
                      class="text-h-neutral-800"
                    ></v-icon>
                    <p v-if="hours">{{ String(hours)?.concat("h") }}</p>
                    <p>{{ String(minutes)?.concat("m") }}</p>
                    <p>{{ String(seconds)?.concat("s") }}</p>
                  </div>
                </CountDown>
                <Button
                  :label="answers.loading ? $t('quiz.saving') : answers.label"
                  class="btn-primary p-button-sm"
                  :disabled="
                    !answers.selected &&
                    !answers.dragAndDropAnswers &&
                    !answers.fillInTheBlanksAnswers &&
                    (!answers.trueOrFalseAnswers ||
                      !answers.allTrueOrFalseAnswered)
                  "
                  :loading="answers.loading"
                  @click="saveAnswers()"
                />
              </div>
            </div>
          </div>
          <div
            class="flex flex-col md:flex-row gap-8 items-center md:items-start"
          >
            <div class="min-w-[150px] w-[25%] md:w-[15%] mt-0 md:mt-8">
              <img
                class="w-full"
                src="@/assets/images/avatares/avatar.svg"
                alt="quiz_avatar"
              />
            </div>
            <div class="mt-5 w-full">
              <QuestionDragDropComponent
                v-if="renderQuestion().type === type.DRAG_AND_DROP"
                :answers="answers"
                :questionCount="questionCount + 1"
                :question="renderQuestion()"
                :key="renderQuestion().id + type.DRAG_AND_DROP"
              />
              <QuestionTrueOrFalseComponent
                v-else-if="renderQuestion().type === type.TRUE_OR_FALSE"
                :answers="answers"
                :questionCount="questionCount + 1"
                :question="renderQuestion()"
                :key="renderQuestion().id + type.TRUE_OR_FALSE"
                @onAnswerSelected="updateTrueOrFalseAnswers"
              />

              <QuestionGapsComponent
                v-else-if="renderQuestion().type === type.FILL_IN_THE_BLANKS"
                :answers="answers"
                :questionCount="questionCount + 1"
                :question="renderQuestion()"
                :key="renderQuestion().id + type.FILL_IN_THE_BLANKS"
              />
              <QuestionComponent
                :question="renderQuestion()"
                :answers="answers"
                :questionCount="questionCount + 1"
                :letter="letter"
                v-else
              />
            </div>
          </div>
        </section>
        <section
          v-else
          id="result-quiz"
          class="flex flex-col gap-3 w-100 sm:w-100 lg:w-8/12 xl:w-8/12"
        >
          <QuizResult
            @onProceed="onProceed"
            @onPercentageQuizResult="onPercentageQuizResult"
            @onClose="onCloseQuiz"
            :learningId="
              quiz?.learningId ? quiz?.learningId : quiz?.learningObjectiveId
            "
            :learningName="learningName"
            :isAdaptative="isAdaptative"
          />
        </section>
      </main>
    </template>
  </FullModal>

  <div v-if="isAdaptative">
    <RecordStream
      v-show="recording"
      ref="recordStream"
      encodeType="select_item"
      :timer="TIMER"
    />

    <SpeechRecognition v-if="hasSpeechRecognition" />

    <QuizInfo ref="quizInfo" :showTips="showTips" />
  </div>

  <Dialog
    v-model:visible="hasTimeout"
    :closable="false"
    :draggable="false"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '40vw' }"
    :modal="true"
    :showHeader="false"
    contentStyle="background-color: #F2F8FF"
  >
    <section class="grid grid-cols-12 mt-8 gap-5 items-center">
      <div class="col-span-12 md:col-span-4 flex justify-center">
        <img
          :src="require('@/assets/images/avatares/repproved.gif')"
          class="w-100 rounded-full"
          alt="repproved img"
          loading="lazy"
        />
      </div>
      <div
        class="col-span-12 md:col-span-8 flex flex-col gap-5 text-center lg:text-start"
      >
        <p class="font-bold text-xl">{{ $t("quiz.timeout.title") }}</p>
        <p class="text-sm w-100">{{ $t("quiz.timeout.info") }}</p>
        <MessageVoice
          :msg="$t('quiz.timeout.title')?.concat($t('quiz.timeout.info'))"
        />
        <Button class="btn-primary p-button-sm" @click="onResult(true)">
          <p>{{ $t("quiz.finalize") }}</p>
        </Button>
      </div>
    </section>
  </Dialog>
</template>

<script lang="ts">
import { ChatBotService } from "@/components/ChatBot/chat-bot.service";
import { MessageVoiceService } from "@/components/MessageVoice/message-voices.service";
import { RecordService } from "@/components/RecordStream/record.service";
import { SpeechActions } from "@/components/SpeechRecognition/actions";
import {
  MotionEnter,
  MotionInitial,
  MotionTransition
} from "@/models/motion.model";
import { QuizRest } from "@/services/rest/quiz.rest";
import { useQuizStore } from "@/store/linear/quiz";
import {
  removeItemLocalStorage,
  setItemLocalStorage
} from "@/utils/save-storage.util";
import { debounceTime, delay, Subscription } from "rxjs";
import { defineAsyncComponent, defineComponent } from "vue";
import { QuizService } from "../quiz.service";
import { QuestionType } from "@/models/quiz.model";
import { useAdaptQuizStore } from "@/store/adaptative/adapt-quiz";


const QuestionComponent = defineAsyncComponent(
  () => import("./question-component.vue")
);

const QuestionDragDropComponent = defineAsyncComponent(
  () => import("./question-drag-drop-component.vue")
);

const QuestionTrueOrFalseComponent = defineAsyncComponent(
  () => import("./question-true-false-component.vue")
);

const QuestionGapsComponent = defineAsyncComponent(
  () => import("./question-gaps-component.vue")
);

const QuizResult = defineAsyncComponent(
  () => import("./Result/quiz-result.vue")
);

const QuizInfo = defineAsyncComponent(() => import("./question-info.vue"));

export default defineComponent({
  name: "quizStudent",
  components: {
    QuestionComponent,
    QuizResult,
    QuizInfo,
    QuestionDragDropComponent,
    QuestionTrueOrFalseComponent,
    QuestionGapsComponent,
  },
  emits: ["onProceed", "onEnded", "onResult", "onClose"],
  computed: {
    rest(): QuizRest {
      return new QuizRest();
    },
    service(): QuizService {
      return new QuizService();
    },
    recordService(): RecordService {
      return new RecordService();
    },
    chatbot(): ChatBotService {
      return new ChatBotService();
    },
    messageService(): MessageVoiceService {
      return new MessageVoiceService();
    },
    hasSpeechRecognition(): boolean {
      let result = false;
      if ("webkitSpeechRecognition" in window) {
        result = true;
      }
      return result;
    },
    type(): typeof QuestionType {
      return QuestionType;
    },
  },
  props: {
    quiz: {
      type: Object,
    },
    quizIntro: {
      type: Object,
    },
    quizEnded: {
      type: Boolean,
      default: false,
    },
    isAdaptative: {
      type: Boolean,
      default: false,
    },
    learningName: [String],
  },
  setup(props) {
    const storeQuiz = props.isAdaptative?useAdaptQuizStore:useQuizStore;
    return { storeQuiz };
  },
  data() {
    return {
      initialMotion: new MotionInitial(),
      enterMotion: new MotionEnter(1, 0, 0, new MotionTransition(30)),
      questionCount: 0 as any,
      answers: {
        data: [],
        selected: null,
        loading: false,
        label: "",
        isDisabled: false,
      } as any,
      headerInfo: {
        title: this.$t("labels.generic", {
          pt: "Quiz",
          en: "Quiz",
        }),
        subtitle: this.$t("labels.generic", {
          pt: "Teste agora o que você aprendeu no objetivo de estudo.",
          en: "Now test what you have learned in the study objective.",
        }),
        icon: "quiz-icon.svg",
      } as any,
      hasTimeout: false as boolean,
      quizEnd: false as boolean,
      recording: false as boolean,
      letter: "" as string,
      showTips: true as boolean,
      tip: false as boolean,
      TIMER: 5000 as number,
      showModalPermissionBrowser: false as boolean,
      finalPercentageQuiz: 0 as number,
    };
  },
  mounted() {
    this.openWithSpeech();
    this.storeQuizData();
  },
  unmounted() {
    removeItemLocalStorage("quizData");
    SpeechActions.quizRecord(false);
    this.recording = false;
  },
  created() {
    this.wsSubscription();
  },
  watch: {
    $route() {
      removeItemLocalStorage("quizData");
    },
  },
  methods: {
    updateTrueOrFalseAnswers(answer) {
      this.answers.trueOrFalseAnswers = answer.trueOrFalseAnswers;

      this.answers.allTrueOrFalseAnswered = answer.allAnswered;
    },
    renderQuestion(): any {
      const last = this.quiz?.questionList?.length - 1 !== this.questionCount;
      if (last) {
        this.answers.label = this.$t("quiz.next");
      } else {
        this.answers.label = this.$t("quiz.finalize");
      }

      return (this.quiz as any)?.questionList[this.questionCount];
    },
    saveAnswers(): void {
      this.answers.loading = true;
      this.answers.isDisabled = true;
      const last = this.quiz?.questionList?.length - 1 !== this.questionCount;
      const data = {
        questionId: this.renderQuestion()?.id,
        itemId: this.answers.selected?.id,
        studentQuizId: this.quiz?.id,
        questionType: this.renderQuestion().type,
        dragAndDropAnswers: this.answers.dragAndDropAnswers,
        trueOrFalseAnswers: this.answers.trueOrFalseAnswers,
        fillInTheBlanksAnswers: this.answers.fillInTheBlanksAnswers,
      };

      const checkAnswerRequest = this.renderQuestion()?.type
        ? this.rest.checkItemAbstractAnswer
        : this.rest.checkItemAnswer;

      checkAnswerRequest(
        this.quiz?.learningId
          ? this.quiz?.learningId
          : this.quiz?.learningObjectiveId,
        data
      )
        .pipe(delay(500))
        .subscribe({
          next: () => {
            if (last) {
              this.nextQuestion();
            } else {
              this.onResult(true);
            }
            this.answers.isDisabled = false;
            this.answers.label = this.$t("quiz.next");
          },
          error: () => {
            this.answers.loading = false;
            this.answers.isDisabled = false;
            this.answers.label = this.$t("quiz.next");
          },
        });
    },
    nextQuestion(): void {
      this.questionCount++;
      this.renderQuestion();
      this.answers.selected = null;
      this.answers.dragAndDropAnswers = null;
      this.answers.trueOrFalseAnswers = null;
      this.answers.fillInTheBlanksAnswers = null;
      this.answers.loading = false;
      this.storeQuizData();
    },
    onProceed(type: string): void {
      this.$emit("onProceed", type);
    },
    onCloseQuiz(): void {
      this.$emit("onClose");
      this.recording = false;
      SpeechActions.quizRecord(false);
    },
    onCountdownEnd(): void {
      this.hasTimeout = true;
    },
    onResult(value?: boolean): void {
      this.hasTimeout = false;
      this.quizEnd = true;
      this.$emit("onEnded", {
        value: value,
        percentage: this.finalPercentageQuiz,
      });
    },
    onPercentageQuizResult(percentage: number): void {
      useAdaptQuizStore.percentage = percentage
      this.finalPercentageQuiz = percentage;
    },
    confirmModal(event?: any): void {
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: `Ao confirmar esta ação, você perderá a tentativa no quiz e ficará sem nota.`,
          en: `Unsaved data will be lost! Are you sure you want to quit?`,
        }),
        header: this.$t("labels.generic", {
          pt: "Sair e perder a tentativa no quiz?",
          en: "Exit Screen",
        }),
        acceptLabel: "Sim, quero sair do quiz.",
        rejectLabel: "Não, vou continuar no quiz.",
        rejectClass: "btn-outline",
        acceptClass: "btn-delete-block",
        accept: () => {
          this.onCloseQuiz();
        },
      });
    },
    openWithSpeech(): void {
      SpeechActions.quiz.pipe(debounceTime(200)).subscribe({
        next: (response) => {
          if (response === true && !this.hasTimeout) {
            this.recording = true;
            this.showTips = false;
            this.messageService.pauseTextToSpeech();
            (this.$refs.recordStream as any)?.sendActionWithRecord();
            setTimeout(() => {
              this.showTips = true;
              SpeechActions.quizRecord(false);
            }, this.TIMER + 1000);
          }
        },
      });
    },
    wsSubscription(): Subscription {
      return this.chatbot
        .wsMessagesActions()
        .pipe(debounceTime(200))
        .subscribe((e) => {
          const parse = JSON.parse(e);
          if (parse.message !== "Client configured") {
            this.letter = parse.message.replaceAll("<br>", "");
            if (this.letter === "N") {
              (this.$refs.quizInfo as any)?.showText(
                {
                  title: "Não entendi o que você falou",
                  subtitle:
                    "Ative o áudio de novo falando “Responder” e depois diga a resposta do item ou fale “Marque o item X”",
                },
                5000
              );
            }
          }
        });
    },
    storeQuizData(): void {
      setItemLocalStorage(
        "quizData",
        JSON.stringify({
          quiz: this.quiz?.id,
          question: this.renderQuestion()?.id,
        })
      );
    },
  },
});
</script>
